import React, { useRef, useLayoutEffect } from 'react'
import { IColumnTypes } from 'components/spreadsheet/types'
import { getRowHeightVariable } from 'components/spreadsheet/helpers/functions'
import { EDITABLE_DATETIME_OPTION_TYPES } from 'components/spreadsheet/constants/const'
import {
  toUTCString,
  formatDate,
  formatDateTime,
  safeFormatDateISOString,
  safeFormatDateTimeISOString
} from 'components/spreadsheet/helpers/format'
import { isDate } from 'helpers/date'
import { cellStyles } from 'components/spreadsheet/components/cell/styles'

const formatEditingValue = (value: string, kind: IColumnTypes): string => {
  if (kind === 'datetime') {
    return formatDateTime(value + 'Z', 'YYYY-MM-DDTHH:mm:ss')
  }
  return formatDate(value, 'YYYY-MM-DD')
}

interface DateCellProps {
  value: string | null
  align: 'center' | 'left' | 'right'
  color: string
  kind: IColumnTypes
  readOnly: boolean
  editing: boolean
  rowHeight: number
  selected: boolean
  validation: boolean
  handleKeyDown: (event: React.KeyboardEvent) => void
  setCellValue: (value: string | null) => void
  dateFormat: string | null
}

const styles = cellStyles()

const DateCellComponent: React.FC<DateCellProps> = ({
  value,
  align,
  color,
  kind,
  readOnly,
  editing,
  rowHeight,
  selected,
  validation,
  handleKeyDown,
  setCellValue,
  dateFormat
}) => {
  const dateRef = useRef<HTMLInputElement>(null)
  const heightPx = getRowHeightVariable(rowHeight)

  useLayoutEffect(() => {
    if (dateRef.current && editing) {
      dateRef.current.focus()
    }
  }, [editing])

  if (EDITABLE_DATETIME_OPTION_TYPES.includes(kind) && editing && !readOnly) {
    return (
      <input
        className={styles.editing({ focused: true, class: 'cell-value-editing resize-none w-[inherit]' })}
        data-focussed
        type={kind === 'datetime' ? 'datetime-local' : kind}
        style={{ height: `${heightPx - 1}px` }}
        defaultValue={value ? formatEditingValue(value, kind) : ''}
        onChange={(event) => setCellValue(toUTCString(event.target.value, kind) || null)}
        onKeyDown={handleKeyDown}
        ref={dateRef}
        step={dateFormat?.includes('ss') ? 1 : undefined}
      ></input>
    )
  }

  const dateValue = (() => {
    if (!value || typeof value !== 'string' || !isDate(kind === 'date' ? value : value + 'Z')) return ''
    return kind === 'date'
      ? safeFormatDateISOString(value, dateFormat ? dateFormat : 'YYYY-MM-DD')
      : safeFormatDateTimeISOString(value, dateFormat ? dateFormat : 'YYYY-MM-DD HH:mm:ss')
  })()

  return (
    <div
      className={styles.value({ focused: selected, class: 'cell-value' })}
      data-focussed={selected || undefined}
      tabIndex={-1}
      style={{
        textAlign: align,
        color: color,
        width: 'var(--cell-width)',
        maxWidth: 'var(--cell-width)',
        maxHeight: `${heightPx}px`
      }}
      onDoubleClick={(event) => !EDITABLE_DATETIME_OPTION_TYPES.includes(kind) && event.stopPropagation()}
    >
      <div
        className={styles.content({ validation, class: 'truncate flex items-center' })}
        style={{
          maxHeight: `${heightPx - 3}px`
        }}
      >
        {dateValue || <span>&nbsp;</span>}
      </div>
    </div>
  )
}

export const DateCell = DateCellComponent
